'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { Check } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { signInWithGoogle } from '@/libs/firebase/auth';
import { usePremiumStatus } from '@/hooks/use-premium-status';
import { useStripeCheckout } from '@/hooks/use-stripe-checkout';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/ui/Button';
import { Toast, useToast } from '@/components/ui/Toast';
import PricingCard from './PricingCard';
import BillingToggle from './BillingToggle';
import Footer from '@/components/layout/Footer';
import { trackFunnelStage, trackCTAClick } from '@/utils/analytics';

export default function PricingPageClient({ locale }) {
  const t = useTranslations('pricing');
  const router = useRouter();
  const searchParams = useSearchParams();
  const source = searchParams?.get('source');
  const { isPremium } = usePremiumStatus();
  const [billingPeriod, setBillingPeriod] = useState('quarterly');
  const [signInLoading, setSignInLoading] = useState(false);
  const { startCheckout, loading: checkoutLoading, error: checkoutError } = useStripeCheckout();
  const { toast, showToast, hideToast } = useToast();
  const auth = getAuth();
  const currentUser = auth?.currentUser;
  const posthog = usePostHog();

  // Track pricing page view
  useEffect(() => {
    if (posthog) {
      trackFunnelStage(posthog, 'pricing_page_viewed', {
        source: source || 'direct',
        referrer: typeof window !== 'undefined' ? document.referrer : null,
        tier: isPremium ? 'premium' : 'free',
        locale,
      });
    }
  }, [posthog, source, isPremium, locale]);

  // Track checkout cancellation
  useEffect(() => {
    const canceled = searchParams?.get('canceled');
    if (canceled === '1' && posthog) {
      posthog.capture('checkout_canceled', {
        source: 'stripe_checkout',
        return_url: typeof window !== 'undefined' ? window.location.href : null,
        price_id: searchParams?.get('priceId') || null,
        locale,
      });
    }
  }, [searchParams, posthog, locale]);

  const handleSubscribe = async (tier, billing = null) => {
    // Track checkout initiation
    if (posthog) {
      trackCTAClick(posthog, 'upgrade', {
        source: 'pricing_page',
        tier_selected: tier,
        billing_period: billing || 'one_time',
        current_tier: isPremium ? 'premium' : 'free',
        locale,
      });
    }

    let userId = currentUser?.uid;
    
    if (!userId) {
      // Track sign-in initiation
      if (posthog) {
        posthog.capture('auth_sign_in_initiated', {
          method: 'google',
          source: 'pricing_page',
          trigger: 'checkout',
          locale,
        });
      }

      setSignInLoading(true);
      try {
        const userFromSignIn = await signInWithGoogle();
        if (!userFromSignIn) {
          showToast('Please sign in to continue.', 'warning');
          return;
        }
        userId = userFromSignIn.uid;

        // Track successful sign-in
        if (posthog) {
          posthog.capture('auth_sign_in_completed', {
            method: 'google',
            source: 'pricing_page',
            user_id: userId,
            locale,
          });
        }
      } catch (error) {
        console.error('Sign-in error:', error);
        showToast('Failed to sign in. Please try again.', 'error');
        return;
      } finally {
        setSignInLoading(false);
      }
    }

    if (isPremium && tier !== 'daily_blessing') {
      router.push(`/${locale}/temple/profile`);
      return;
    }

    let priceId;
    let productType;
    if (tier === 'oracle_guardian') {
      priceId = billing === 'quarterly' 
        ? process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_QUARTERLY
        : process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_MONTHLY;
      productType = billing === 'quarterly' ? 'subscription_quarterly' : 'subscription_monthly';
    } else if (tier === 'daily_blessing') {
      priceId = process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_24H_NOVEMBER_PROMO;
      productType = 'premium_access_24h';
    }

    if (!priceId) {
      showToast('Pricing configuration error. Please try again later.', 'error');
      return;
    }

    // Track checkout initiated
    if (posthog) {
      trackFunnelStage(posthog, 'checkout_initiated', {
        price_id: priceId,
        product_type: productType,
        tier_selected: tier,
        billing_period: billing || 'one_time',
        user_id: userId,
        locale,
      });
    }

    try {
      await startCheckout(userId, priceId, locale);
    } catch (error) {
      console.error('Checkout error:', error);
      showToast('Failed to start checkout. Please try again.', 'error');
      
      // Track checkout error
      if (posthog) {
        posthog.capture('checkout_error', {
          error_message: error.message,
          price_id: priceId,
          locale,
        });
      }
    }
  };


  // Show checkout errors via toast when they occur
  useEffect(() => {
    if (checkoutError) {
      showToast(checkoutError, 'error');
    }
  }, [checkoutError, showToast]);

  const truthSeekerFeatures = t.raw('truthSeeker.features');
  const oracleGuardianFeatures = t.raw('oracleGuardian.features');

  return (
    <div className="min-h-screen bg-[#050316] text-[#FBFBFE] py-12 md:py-20 px-4">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={hideToast}
        />
      )}
      <div className="max-w-7xl mx-auto">
        {/* Continuity Banner */}
        {source === 'temple_insight' && (
          <div className="mb-6 rounded-lg border border-purple-500/40 bg-purple-900/30 p-4">
            <p className="text-sm text-purple-200">
              Luna paused while revealing your pattern.
              <br />
              <strong>Continue where your insight left off.</strong>
            </p>
          </div>
        )}

        {/* Header */}
        <h1 
          className="text-4xl md:text-5xl font-marcellus text-center mb-12 text-[#FBFBFE]"
          style={{ fontFamily: 'var(--font-marcellus), serif' }}
        >
          {t('header')}
        </h1>

        {/* Billing Toggle - Separated and centered above Oracle Guardian card */}
        <div className="max-w-6xl mx-auto mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-5">
            <div></div>
            <div className="flex justify-center">
              <BillingToggle
                value={billingPeriod}
                onChange={setBillingPeriod}
                monthlyLabel={t('oracleGuardian.billing.monthly')}
                quarterlyLabel={t('oracleGuardian.billing.quarterly')}
                badgeText={t('oracleGuardian.badge')}
              />
            </div>
            <div></div>
          </div>
        </div>

        {/* Three-Card Container */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-5 max-w-6xl mx-auto mb-16 md:mb-24">
          {/* Truth Seeker Card */}
          <div className="flex items-start">
          <PricingCard
            title={t('truthSeeker.title')}
            price={t('truthSeeker.price')}
            tagline={t('truthSeeker.tagline')}
            description={t('truthSeeker.description')}
            features={Array.isArray(truthSeekerFeatures) ? truthSeekerFeatures : []}
              showCTA={true}
              ctaText="Current Plan"
            variant="free"
              isCurrentPlan={true}
          />
          </div>

          {/* Oracle Guardian Card */}
          <div className="relative flex items-start">
            <PricingCard
              title={t('oracleGuardian.title')}
              price={billingPeriod === 'monthly' ? t('oracleGuardian.price.monthly') : t('oracleGuardian.price.quarterly')}
              priceDetails={billingPeriod === 'quarterly' ? t('oracleGuardian.price.quarterlyDetails') : null}
              tagline={t('oracleGuardian.tagline')}
              description={t('oracleGuardian.description')}
              features={Array.isArray(oracleGuardianFeatures) ? oracleGuardianFeatures : []}
              showCTA={true}
              ctaText={t('oracleGuardian.cta')}
              onCTA={() => handleSubscribe('oracle_guardian', billingPeriod)}
              variant="premium"
              loading={checkoutLoading || signInLoading}
              />
          </div>

          {/* Daily Blessing Card */}
          <div className="flex items-start">
          <PricingCard
            title={t('dailyBlessing.title')}
            price={t('dailyBlessing.price')}
            description={t('dailyBlessing.description')}
            textContent={t('dailyBlessing.textContent')}
            showCTA={true}
            ctaText={t('dailyBlessing.cta')}
            onCTA={() => handleSubscribe('daily_blessing')}
            variant="daily"
            loading={checkoutLoading || signInLoading}
          />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
